import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, withLatestFrom } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, tap } from 'rxjs/operators';
import { StatusService } from '~status/services/status.service';
import { StatusActions } from '~status/store/status.actions';
import { selectDeviceStatus } from '~status/store/status.selectors';
import { processRestError } from '~shared/error-helper';

export const statusEffect = createEffect(
    (actions$: Actions = inject(Actions), statusService = inject(StatusService)) => {
        return actions$.pipe(
            ofType(StatusActions.loadDeviceStatus),
            switchMap(({ deviceId }) =>
                statusService.getStatus(deviceId).pipe(
                    tap((data) => localStorage.setItem('deviceId', data.id)),
                    map((data) => StatusActions.loadDeviceStatusSuccess({ data })),
                    catchError((error: unknown) =>
                        of(StatusActions.loadDeviceStatusFailure({ error: processRestError(error) }))
                    )
                )
            )
        );
    },
    { functional: true }
);

export const deleteAttachmentEffect = createEffect(
    (actions$: Actions = inject(Actions), statusService = inject(StatusService)) => {
        return actions$.pipe(
            ofType(StatusActions.deleteAttachment),
            exhaustMap(({ deviceId, id }) =>
                statusService.deleteAttachment(deviceId, id).pipe(
                    map(() => StatusActions.deleteAttachmentSuccess({ deviceId, id })),
                    catchError((error: unknown) =>
                        of(StatusActions.deleteAttachmentFailure({ error: processRestError(error) }))
                    )
                )
            )
        );
    },
    { functional: true }
);

export const impersonationEffect = createEffect(
    (actions$: Actions = inject(Actions), statusService = inject(StatusService)) => {
        return actions$.pipe(
            ofType(StatusActions.loadImpersonationToken),
            switchMap(() =>
                statusService.getImpersonationToken().pipe(
                    map(({ email, token }) => StatusActions.loadImpersonationTokenSuccess({ email, token })),
                    catchError((error: unknown) =>
                        of(StatusActions.loadImpersonationTokenFailure({ error: processRestError(error) }))
                    )
                )
            )
        );
    },
    { functional: true }
);

export const navigateEffect = createEffect(
    (actions$: Actions = inject(Actions), store: Store = inject(Store)) => {
        return actions$.pipe(
            ofType(StatusActions.loadImpersonationTokenSuccess),
            withLatestFrom(store.select(selectDeviceStatus)),
            tap(([{ email, token }, device]) => {
                const { vehicleId, customerId } = device ?? {};
                if (vehicleId && customerId) {
                    const baseUrl = 'https://live.keatech.com';
                    const url = new URL('/login/impersonate', baseUrl);
                    url.searchParams.append('vehicle', vehicleId.toString());
                    url.searchParams.append('customer', customerId.toString());
                    url.searchParams.append('email', email);
                    url.searchParams.append('token', token);
                    window.location.assign(url.toString());
                }
            })
        );
    },
    { functional: true, dispatch: false }
);
